import { useQuery } from "@apollo/client/react";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { FiPlus, FiTrash2 } from "react-icons/fi";
import { GetPhonemesGroupedDocument, type PhonemeType } from "../../../generated/graphql";
import { PageHeader } from "../shared";
import { AddPhonicsModal } from "./AddPhonicsModal";
import { PhonemeTypeSection } from "./PhonemeTypeSection";
import { PHONEME_TYPES } from "./constants";

export function PhonicsPage() {
  const [selectedType, setSelectedType] = useState<PhonemeType | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);

  const { data, loading, refetch } = useQuery(GetPhonemesGroupedDocument, {
    variables: { phonemeType: selectedType },
    fetchPolicy: "cache-first",
  });

  const groupedByType = data?.phonemesGrouped?.reduce(
    (acc, group) => {
      const type = group.phonemeType;
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(group);
      return acc;
    },
    {} as Record<string, typeof data.phonemesGrouped>
  );

  // counts for each type items (phonemeType)
  const typeCounts = PHONEME_TYPES.reduce(
    (acc, type) => {
      acc[type.value] = groupedByType?.[type.value]?.length || 0;
      return acc;
    },
    {} as Record<string, number>
  );

  const totalCount = Object.values(typeCounts).reduce((sum, count) => sum + count, 0);

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <PageHeader
        title="Phonics"
        description="Manage phonics audio files for teaching phonemes"
        actions={
          <>
            <Link
              to="/media/phonics/trash"
              className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <FiTrash2 size={16} />
              Trash
            </Link>
            <button
              type="button"
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              <FiPlus size={16} />
              Add Phonics
            </button>
          </>
        }
      />

      {/* Filters */}
      <div className="border-b border-gray-200 bg-white px-6 py-3">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-gray-700">Filter:</span>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setSelectedType(null)}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                selectedType === null
                  ? "bg-blue-600 text-white shadow-sm"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              All
              {selectedType === null && (
                <span className="rounded-full bg-blue-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                  {totalCount}
                </span>
              )}
            </button>
            {PHONEME_TYPES.map((type) => {
              const count = typeCounts[type.value];
              if (count === 0) return null;

              return (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => setSelectedType(type.value)}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                    selectedType === type.value
                      ? "bg-blue-600 text-white shadow-sm"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {type.label}
                  <span
                    className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                      selectedType === type.value
                        ? "bg-blue-500 text-white"
                        : "bg-gray-200 text-gray-600"
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-gray-500">Loading phonics...</div>
          </div>
        ) : !groupedByType || Object.keys(groupedByType).length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <p className="text-gray-500">No phonics found</p>
            <button
              type="button"
              onClick={() => setShowAddModal(true)}
              className="mt-4 text-blue-600 hover:text-blue-700"
            >
              Add your first phoneme
            </button>
          </div>
        ) : (
          <div className="space-y-8">
            {PHONEME_TYPES.map((type) => {
              const groups = groupedByType[type.value];
              if (!groups || groups.length === 0) return null;

              return (
                <PhonemeTypeSection
                  key={type.value}
                  phonemeType={type.value}
                  label={type.label}
                  groups={groups}
                  // onUpdate={() => refetch()}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Add Phonics Modal */}
      {showAddModal && (
        <AddPhonicsModal
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            refetch();
          }}
        />
      )}
    </div>
  );
}
